// Ported from twifi-theme-html/src/scripts/helpers/bundle-builder.js
//
// Discount model: each preset (rendered as a `.bundle-builder__preset` card/option, with its
// discount rate on `data-preset-discount`) exactly matches one product set. The active discount
// is whichever preset's product set exactly equals the currently-checked services — there is no
// generic quantity/subtotal tier fallback; a checked set that doesn't match any preset gets no
// discount at all. `amenities_apply_bundle_discount_fee()` (includes/bundle-builder.php) re-runs
// the same exact-match check server-side against the live cart, so this stays correct after the
// user edits the cart on the cart page itself.
function roundMoney(value) {
    return Math.round(value * 100) / 100;
}

function formatCHF(value) {
    const rounded = roundMoney(value);

    return Number.isInteger(rounded) ? `CHF ${rounded}` : `CHF ${rounded.toFixed(2)}`;
}

function getServices(root) {
    return Array.from(root.querySelectorAll('.bundle-builder__service')).map((el) => ({
        el,
        id: el.dataset.serviceId,
        price: parseFloat(el.dataset.price),
        title: el.querySelector('.bundle-builder__service-title').textContent,
        input: el.querySelector('.bundle-builder__service-input'),
    }));
}

function findMatchingPreset(root, checkedIds) {
    const checkedSet = new Set(checkedIds);
    const presets = Array.from(root.querySelectorAll('.bundle-builder__preset'));

    return (
        presets.find((preset) => {
            const presetIds = preset.dataset.presetServices.split(',');

            return presetIds.length === checkedSet.size && presetIds.every((serviceId) => checkedSet.has(serviceId));
        }) || null
    );
}

function computeTotals(checked, matchedPreset) {
    const subtotal = roundMoney(checked.reduce((sum, service) => sum + service.price, 0));
    const rate = matchedPreset ? parseFloat(matchedPreset.dataset.presetDiscount) : 0;
    const discount = roundMoney(subtotal * rate);
    const total = roundMoney(subtotal - discount);

    return { subtotal, discount, total };
}

function buildSummaryLine(template, label, value) {
    const fragment = template.content.cloneNode(true);

    fragment.querySelector('.bundle-builder__summary-label').textContent = label;
    fragment.querySelector('.bundle-builder__summary-value').textContent = value;

    return fragment.firstElementChild;
}

function renderSummary(root, checked, totals) {
    const linesEl = root.querySelector('[data-bundle-builder-summary-lines]');
    const emptyEl = root.querySelector('[data-bundle-builder-summary-empty]');
    const dividerEl = root.querySelector('[data-bundle-builder-summary-divider]');
    const totalWrapEl = root.querySelector('[data-bundle-builder-summary-total]');
    const totalValueEl = root.querySelector('[data-bundle-builder-total-value]');
    const ctaEl = root.querySelector('[data-bundle-builder-cta]');
    const template = root.querySelector('[data-bundle-builder-line-template]');

    linesEl.replaceChildren();

    checked.forEach((service) => {
        linesEl.appendChild(buildSummaryLine(template, service.title, formatCHF(service.price)));
    });

    if (totals.discount > 0) {
        const discountLine = buildSummaryLine(template, root.dataset.discountLabel, `−${formatCHF(totals.discount)}`);

        discountLine.classList.add('bundle-builder__summary-line--discount');
        linesEl.appendChild(discountLine);
    }

    totalValueEl.textContent = formatCHF(totals.total);

    const isEmpty = checked.length === 0;

    emptyEl.hidden = !isEmpty;
    linesEl.hidden = isEmpty;
    dividerEl.hidden = isEmpty;
    totalWrapEl.hidden = isEmpty;
    ctaEl.disabled = isEmpty;
}

function syncPresetFromServices(root, matchedPreset) {
    const presets = Array.from(root.querySelectorAll('.bundle-builder__preset'));
    const select = root.querySelector('[data-bundle-builder-preset-select]');

    presets.forEach((preset) => {
        const isMatch = preset === matchedPreset;

        preset.classList.toggle('bundle-builder__preset--active', isMatch);
        preset.querySelector('.bundle-builder__preset-input').checked = isMatch;
    });

    if (select) select.value = matchedPreset ? matchedPreset.dataset.presetId : '';
}

function recompute(root) {
    const services = getServices(root);

    services.forEach((service) => {
        service.el.classList.toggle('bundle-builder__service--checked', service.input.checked);
    });

    const checked = services.filter((service) => service.input.checked);
    const matchedPreset = findMatchingPreset(
        root,
        checked.map((service) => service.id)
    );
    const totals = computeTotals(checked, matchedPreset);

    renderSummary(root, checked, totals);
    syncPresetFromServices(root, matchedPreset);
}

function applyPreset(root, presetId) {
    const preset = root.querySelector(`.bundle-builder__preset[data-preset-id="${presetId}"]`);

    if (!preset) return;

    const presetIds = new Set(preset.dataset.presetServices.split(','));

    getServices(root).forEach((service) => {
        service.input.checked = presetIds.has(service.id);
    });

    recompute(root);
}

function showError(container, message) {
    let notice = container.querySelector('.woocommerce-error');

    if (!notice) {
        notice = document.createElement('ul');
        notice.className = 'woocommerce-error';
        notice.setAttribute('role', 'alert');
        container.prepend(notice);
    }

    notice.innerHTML = '';

    const item = document.createElement('li');
    item.textContent = message;
    notice.appendChild(item);

    notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function submitBundle(root, cta) {
    const productIds = getServices(root)
        .filter((service) => service.input.checked)
        .map((service) => service.id);

    if (!productIds.length || typeof twf === 'undefined') return;

    cta.disabled = true;
    cta.setAttribute('aria-busy', 'true');

    const body = new URLSearchParams();

    body.append('action', 'amenities_bundle_add_to_cart');
    body.append('nonce', twf.bundle_nonce);
    productIds.forEach((productId) => body.append('product_ids[]', productId));

    fetch(twf.ajax_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
    })
        .then((response) => response.json())
        .then((data) => {
            if (data && data.status === 'success' && data.data && data.data.redirect) {
                window.location.href = data.data.redirect;

                return;
            }

            showError(root, (data && data.message) || twf.products_error_msg);
            cta.disabled = false;
            cta.removeAttribute('aria-busy');
        })
        .catch(() => {
            showError(root, twf.products_error_msg);
            cta.disabled = false;
            cta.removeAttribute('aria-busy');
        });
}

function initBundleBuilder(root) {
    root.querySelectorAll('.bundle-builder__service-input').forEach((input) => {
        input.addEventListener('change', () => recompute(root));
    });

    root.querySelectorAll('.bundle-builder__preset-input').forEach((input) => {
        input.addEventListener('change', () => applyPreset(root, input.value));
    });

    const select = root.querySelector('[data-bundle-builder-preset-select]');

    if (select) {
        select.addEventListener('change', () => applyPreset(root, select.value));
    }

    const cta = root.querySelector('[data-bundle-builder-cta]');

    if (cta) {
        cta.addEventListener('click', () => submitBundle(root, cta));
    }

    recompute(root);
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-bundle-builder]').forEach(initBundleBuilder);
});
